import { useState } from "react";
import { Avatar, findInParty } from "./Avatar.js";
import type { Combatant, HpChangeKind } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";
import { DeathSaves, HpBar, HpControls } from "./Hp.js";

export function CombatTracker() {
  const { combat, isHost, campaign, setCombat, fail, user } = useGame();
  const [busy, setBusy] = useState(false);

  const act = async (action: "start" | "next" | "end" | "roll-initiative" | "add-party") => {
    if (action === "end" && !window.confirm("End combat?")) return;
    setBusy(true);
    try {
      const r = await api.combatAction(campaign.id, action);
      setCombat(r.combat);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!combat) {
    return (
      <section className="panel">
        <h3>Combat</h3>
        <p className="muted">No combat right now.</p>
        {isHost ? (
          <button className="primary" disabled={busy} onClick={() => act("start")}>
            Start combat
          </button>
        ) : (
          <p className="muted small">The host (or the DM by voice) starts combat.</p>
        )}
      </section>
    );
  }

  const started = combat.currentCombatantId !== null;
  const current = combat.combatants.find((c) => c.id === combat.currentCombatantId);
  const missing = combat.combatants.filter((c) => c.initiative === null).length;

  return (
    <section className="panel combat">
      <h3>
        Combat <span className="round">{started ? `Round ${combat.round}` : "Rolling initiative"}</span>
      </h3>
      {current && (
        <p className="turn-banner">
          {current.ownerDiscordId === user.discordId ? "Your turn" : `${current.name}'s turn`}
        </p>
      )}
      <ol className="initiative scroll-area">
        {combat.combatants.map((c) => (
          <CombatantRow key={c.id} c={c} current={c.id === combat.currentCombatantId} />
        ))}
      </ol>
      {combat.combatants.length === 0 && <p className="muted">Nobody in the fight yet.</p>}
      {isHost && (
        <>
          <div className="combat-controls">
            <button className="primary" disabled={busy || combat.combatants.length === 0} onClick={() => act("next")}>
              {started ? "Next turn ▸" : "Begin round 1 ▸"}
            </button>
            {missing > 0 && (
              <button disabled={busy} onClick={() => act("roll-initiative")} title="d20 + DEX mod / init bonus for everyone missing">
                Auto-roll initiative ({missing})
              </button>
            )}
            <button disabled={busy} onClick={() => act("add-party")} title="Add party members who joined after combat started">
              Add party
            </button>
            <button className="danger" disabled={busy} onClick={() => act("end")}>
              End combat
            </button>
          </div>
          <AddMonster />
        </>
      )}
    </section>
  );
}

function CombatantRow({ c, current }: { c: Combatant; current: boolean }) {
  const { isHost, user, campaign, setCombat, fail, party } = useGame();
  const mine = c.ownerDiscordId === user.discordId;
  const canHp = isHost || mine;
  const [editingInit, setEditingInit] = useState(false);
  const [initValue, setInitValue] = useState("");

  const hp = async (kind: HpChangeKind, amount: number) => {
    try {
      setCombat((await api.combatantHp(campaign.id, c.id, kind, amount)).combat);
    } catch (err) {
      fail(err);
    }
  };
  const setInit = async (value: number | null) => {
    try {
      setCombat((await api.setInitiative(campaign.id, c.id, value)).combat);
      setEditingInit(false);
    } catch (err) {
      fail(err);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${c.name} from combat?`)) return;
    try {
      setCombat((await api.removeCombatant(campaign.id, c.id)).combat);
    } catch (err) {
      fail(err);
    }
  };
  const deathSave = async () => {
    try {
      await api.deathSave(campaign.id, c.characterId!);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <li className={`${current ? "current" : ""} ${c.down ? "is-down" : ""} ${c.kind}`}>
      <div className="init">
        {editingInit ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void setInit(initValue === "" ? null : Number(initValue));
            }}
          >
            <input type="number" autoFocus value={initValue} onChange={(e) => setInitValue(e.target.value)} onBlur={() => setEditingInit(false)} />
          </form>
        ) : c.initiative === null ? (
          canHp ? (
            <button className="small-btn" onClick={() => setInit(null)} title="Roll d20 + initiative bonus">
              Roll
            </button>
          ) : (
            <span className="muted">?</span>
          )
        ) : (
          <span
            className={isHost ? "editable" : ""}
            title={isHost ? "Click to change" : undefined}
            onClick={() => {
              if (!isHost) return;
              setInitValue(String(c.initiative));
              setEditingInit(true);
            }}
          >
            {c.initiative}
          </span>
        )}
      </div>
      {c.kind === "character" && <Avatar c={findInParty(party, { characterId: c.characterId })} name={c.name} size={28} />}
      <div className="who">
        <strong>{c.name}</strong>
        {c.kind === "monster" && <span className="tag">monster</span>}
        {c.down && <span className="tag down">{c.kind === "monster" ? "defeated" : "down"}</span>}
        {c.conditions.map((k) => (
          <span key={k} className="tag cond-tag">
            {k}
          </span>
        ))}
        {c.kind === "character" && c.down && c.deathSaves && (
          <>
            <DeathSaves {...c.deathSaves} />
            {canHp && (
              <button className="small-btn" onClick={deathSave}>
                Death save
              </button>
            )}
          </>
        )}
      </div>
      <HpBar hp={c.hp} maxHp={c.maxHp} tempHp={c.tempHp} />
      <span className="ac" title="Armor Class">
        AC {c.ac}
      </span>
      {canHp ? <HpControls onChange={hp} /> : <span />}
      {isHost ? (
        <button className="icon" title="Remove" onClick={remove}>
          ✕
        </button>
      ) : (
        <span />
      )}
    </li>
  );
}

function AddMonster() {
  const { campaign, setCombat, fail } = useGame();
  const [name, setName] = useState("");
  const [count, setCount] = useState(1);
  const [hp, setHp] = useState(7);
  const [ac, setAc] = useState(13);
  const [initiative, setInitiative] = useState("");
  const [initBonus, setInitBonus] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.addMonsters(campaign.id, {
        name: name.trim(),
        count,
        hp,
        ac,
        initiative: initiative === "" ? null : Number(initiative),
        initBonus,
      });
      setCombat(r.combat);
      setName("");
      setInitiative("");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="add-monster" onSubmit={submit}>
      <label className="grow">
        Monster
        <input value={name} placeholder="Goblin" maxLength={50} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        #<input type="number" min={1} max={12} value={count} onChange={(e) => setCount(Number(e.target.value) || 1)} />
      </label>
      <label>
        HP<input type="number" min={1} value={hp} onChange={(e) => setHp(Number(e.target.value) || 1)} />
      </label>
      <label>
        AC<input type="number" min={0} value={ac} onChange={(e) => setAc(Number(e.target.value) || 0)} />
      </label>
      <label>
        Init<input type="number" placeholder="auto" value={initiative} onChange={(e) => setInitiative(e.target.value)} />
      </label>
      <label>
        Init +<input type="number" value={initBonus} onChange={(e) => setInitBonus(Number(e.target.value) || 0)} />
      </label>
      <button className="primary" disabled={busy || !name.trim()}>
        Add
      </button>
    </form>
  );
}
